import type { ProcessingLog } from '@/types/reprocessamento';
import {
  fetchOrdensServico,
  fetchFuncionarios,
  fetchClientes,
} from '@/utils/ordensServico/reprocessamentoDataFetcher';
import {
  precomputeAggregates,
  calculateCustosBatch,
} from '@/utils/ordensServico/reprocessamentoCostCalculator';
import { updateOSInDatabase } from '@/utils/ordensServico/reprocessamentoDatabaseUpdater';

const CALC_BATCH = 500;

export type ReprocessamentoCustosJobParams = {
  dataInicio: string;
  dataFim: string;
  numeroOS: string;
  funcionario?: string;
  impostoPercentual: number;
  onProgress?: (s: { processed: number; total: number; updated: number; errors: number }) => void;
};

/**
 * Core reprocessamento de custos (sem toast/React). Usado pela agenda e pelo hook da tela de OS.
 */
export async function executeReprocessamentoCustosJob(
  params: ReprocessamentoCustosJobParams
): Promise<{ total: number; updated: number; errors: number; logs: ProcessingLog[] }> {
  const { dataInicio, dataFim, numeroOS, funcionario, impostoPercentual, onProgress } = params;

  if (!dataInicio && !dataFim && !numeroOS && (!funcionario || funcionario === '')) {
    throw new Error('Informe pelo menos um filtro: período, número da OS ou funcionário.');
  }

  const [ordensServico, funcionarioMap, clienteMap] = await Promise.all([
    fetchOrdensServico(dataInicio, dataFim, numeroOS, funcionario),
    fetchFuncionarios(),
    fetchClientes(),
  ]);

  if (ordensServico.length === 0) {
    return { total: 0, updated: 0, errors: 0, logs: [] };
  }

  const aggregates = precomputeAggregates(ordensServico);
  let processedCount = 0;
  let updatedCount = 0;
  let errorCount = 0;
  const allLogs: ProcessingLog[] = [];

  for (let i = 0; i < ordensServico.length; i += CALC_BATCH) {
    const batch = ordensServico.slice(i, i + CALC_BATCH);
    const { updates, logs, errors: batchErrors } = calculateCustosBatch(
      batch,
      funcionarioMap,
      clienteMap,
      aggregates,
      impostoPercentual
    );

    errorCount += batchErrors;
    allLogs.push(...logs);
    if (updates.length > 0) {
      const dbResult = await updateOSInDatabase(updates);
      updatedCount += dbResult.updated;
      errorCount += dbResult.errors;
    }
    processedCount += batch.length;
    onProgress?.({
      total: ordensServico.length,
      processed: processedCount,
      updated: updatedCount,
      errors: errorCount,
    });
  }

  return { total: ordensServico.length, updated: updatedCount, errors: errorCount, logs: allLogs };
}
